import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import {
  LayoutDashboard, Users, Building2, Contact,
  Settings as SettingsIcon, LogOut, Trophy,
  ServerCog, UserCog, ClipboardList, Briefcase, UserCheck, CalendarDays, GraduationCap, Activity, UsersRound,
  BrainCircuit, Megaphone, Medal, Rocket,
  ChevronDown,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { cn } from "@/components/ui/cn";
import { Avatar } from "@/components/ui/avatar";

type IconType = typeof Users;

type NavHint = { to: string; label: string; icon: IconType; adminOnly?: boolean };

/** Every page ships here, always visible with its full label (expanded) or as
 *  a compact icon (rail) — standard app-sidebar behaviour. */
const SECTIONS: { label: string; items: NavHint[] }[] = [
  { label: "Workspace", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/leads", label: "Job Leads", icon: Briefcase },
    { to: "/hackathons", label: "Hackathon Leads", icon: Trophy },
    { to: "/job-fairs", label: "Job Fairs", icon: CalendarDays },
    { to: "/my-leads", label: "My Leads", icon: UserCheck },
  ] },
  { label: "Contacts", items: [
    { to: "/college-data", label: "College Data", icon: GraduationCap },
    { to: "/tpo-directory", label: "TPO Directory", icon: ClipboardList },
    { to: "/chro-directory", label: "CHRO Data", icon: Users },
    { to: "/contacts", label: "HR Contacts", icon: Contact },
    { to: "/companies", label: "Companies", icon: Building2 },
    { to: "/candidates", label: "Candidates", icon: UsersRound },
  ] },
  { label: "Intel", items: [
    { to: "/campus-intel", label: "Campus Placement", icon: GraduationCap },
    { to: "/intelligence", label: "Fleet Intelligence", icon: Activity, adminOnly: true },
  ] },
  // UptoSkills corporate lead-generation campaigns: one page per campaign —
  // never one generic table.
  { label: "Campaigns", items: [
    { to: "/ai-hiring", label: "AI-Powered Hiring", icon: BrainCircuit },
    { to: "/campus-drives", label: "Campus Placement Drives", icon: GraduationCap },
    { to: "/employer-branding", label: "Employer Branding", icon: Megaphone },
    { to: "/skill-league", label: "Skill League Sponsorship", icon: Medal },
    { to: "/hackathon-sponsorship", label: "Hackathon Sponsorship", icon: Rocket },
  ] },
  { label: "System", items: [
    { to: "/armies", label: "Scraper Armies", icon: ServerCog, adminOnly: true },
    { to: "/settings", label: "Settings", icon: SettingsIcon, adminOnly: true },
    { to: "/team", label: "Team", icon: UserCog, adminOnly: true },
  ] },
];

/** Auto-hide sidebar nav: on desktop the bar is a thin permanent icon rail
 *  (collapsed=true — titles only via native tooltips); the accordion dropdown
 *  layout serves the mobile drawer. */

const pathnameUnder = (to: string, pathname: string) => pathname === to || pathname.startsWith(`${to}/`);

/** Nav row — the core surface. Expanded: icon + full label. Rail: icon only. */
function NavItem({ to, label, icon: Icon, collapsed, onNavigate }: NavHint & { collapsed: boolean; onNavigate?: () => void }) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      title={label}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-2 outline-offset-0 transition-colors",
          "text-sidebar-foreground hover:bg-sidebar-muted hover:text-foreground",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/60",
          collapsed ? "mx-auto h-10 w-10 justify-center rounded-lg" : "h-9 w-full rounded-lg px-1.5 text-[12.5px] font-medium",
          isActive && "!bg-sidebar-active !text-sidebar-active-foreground !font-semibold",
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden
            className={cn(
              "absolute -left-2 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary transition-opacity duration-200",
              isActive ? "opacity-100" : "opacity-0",
            )}
          />
          <Icon className={cn("h-4 w-4 shrink-0", isActive && "text-primary")} strokeWidth={isActive ? 2.4 : 1.9} />
          <span className={cn("min-w-0 flex-1 truncate", collapsed && "hidden")}>{label}</span>
        </>
      )}
    </NavLink>
  );
}

/** One category open at a time (accordion): clicking a header toggles its
 *  dropdown and auto-hides the previous one; navigating into a page opens its
 *  category so the active group is always visible. */
function NavList({ collapsed, isAdmin, location, onNavigate }: { collapsed: boolean; isAdmin: boolean; location: { pathname: string }; onNavigate?: () => void }) {
  const sectionOf = (pathname: string) => SECTIONS.find((s) => s.items.some((it) => pathnameUnder(it.to, pathname)))?.label ?? null;
  const [openSection, setOpenSection] = useState<string | null>(() => sectionOf(location.pathname));
  useEffect(() => { setOpenSection(sectionOf(location.pathname)); }, [location.pathname]);

  if (collapsed) {
    return (
      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden px-[6px] py-3" aria-label="All pages">
        {SECTIONS.map((sec) => {
          const items = sec.items.filter((it) => !it.adminOnly || isAdmin);
          if (!items.length) return null;
          return (
            <div key={sec.label} className="flex flex-col gap-0.5">
              <div className="mx-1 mb-1 h-px bg-sidebar-border" />
              {items.map((it) => <NavItem key={it.to} {...it} collapsed onNavigate={onNavigate} />)}
            </div>
          );
        })}
      </nav>
    );
  }
  return (
    <nav className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto overflow-x-hidden px-2 py-3" aria-label="All pages">
      {SECTIONS.map((sec) => {
        const items = sec.items.filter((it) => !it.adminOnly || isAdmin);
        if (!items.length) return null;
        const hasActive = items.some((it) => pathnameUnder(it.to, location.pathname));
        const open = openSection === sec.label;
        return (
          <div key={sec.label} className="flex flex-col">
            <button
              type="button"
              onClick={() => setOpenSection(open ? null : sec.label)}
              aria-expanded={open}
              className={cn(
                "flex w-full items-center justify-between rounded-lg px-2 py-1 transition-colors hover:text-foreground",
                "text-[10px] font-semibold uppercase tracking-[0.18em]",
                hasActive ? "text-primary" : "text-muted-foreground/60",
              )}
            >
              {sec.label}
              <ChevronDown className={cn("h-3 w-3 shrink-0 transition-transform duration-200", open && "rotate-180")} strokeWidth={2.5} />
            </button>
            {open && (
              <div className="mt-0.5 flex flex-col gap-0.5">
                {items.map((it) => <NavItem key={it.to} {...it} collapsed={false} onNavigate={onNavigate} />)}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function Footer({ collapsed, email, role, onLogout }: { collapsed: boolean; email?: string | null; role?: string | null; onLogout: () => void }) {
  if (collapsed) {
    return (
      <div className="flex shrink-0 flex-col items-center gap-1 border-t border-sidebar-border py-2.5">
        <div title={email || undefined}><Avatar name={email} size="sm" /></div>
        <button
          type="button"
          onClick={onLogout}
          title="Sign out"
          aria-label="Sign out"
          className="grid h-7 w-7 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive-soft hover:text-destructive"
        >
          <LogOut className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex shrink-0 items-center gap-2.5 border-t border-sidebar-border p-2.5">
      <Avatar name={email} size="sm" />
      <div className="min-w-0 flex-1 leading-tight" title={email || undefined}>
        <p className="truncate text-[12.5px] font-medium text-foreground">{email || "User"}</p>
        <p className="truncate text-[11px] capitalize text-muted-foreground">{role || "user"}</p>
      </div>
      <button
        type="button"
        onClick={onLogout}
        title="Sign out"
        aria-label="Sign out"
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive-soft hover:text-destructive"
      >
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

const Sidebar = ({ drawerOpen = false, onDrawerClose }: { drawerOpen?: boolean; onDrawerClose?: () => void }) => {
  const { user, logout, revokeCurrentToken } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const onLogout = async () => { await revokeCurrentToken(); await logout(); navigate("/login"); };

  return (
    <>
      {/* Desktop sidebar — thinnest labelled bar: icon + page text, accordion
          categories (one open at a time; opening one auto-hides the previous). */}
      <aside className="hidden h-full w-[216px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar md:flex">
        <div className="flex h-14 shrink-0 items-center gap-1.5 border-b border-sidebar-border px-2">
          <span title="HireGen" className="grid place-items-center"><LogoMark className="h-7 w-7" /></span>
          <span className="min-w-0 flex-1 truncate text-[14px] font-bold tracking-tight text-foreground">HireGen</span>
        </div>
        <NavList collapsed={false} isAdmin={isAdmin} location={location} />
        <Footer collapsed={false} email={user?.email} role={user?.role} onLogout={onLogout} />
      </aside>

      {/* Mobile drawer — expanded layout, closes on navigate */}
      {drawerOpen && (
        <div className="fixed inset-0 z-overlay md:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-overlay-in" onClick={onDrawerClose} aria-hidden />
          <aside className="absolute inset-y-0 left-0 flex w-[248px] flex-col border-r border-sidebar-border bg-sidebar shadow-float animate-drawer-in">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-sidebar-border px-3">
              <div className="flex items-center gap-1.5">
                <LogoMark className="h-7 w-7" />
                <span className="text-[14px] font-bold tracking-tight text-foreground">HireGen</span>
              </div>
              <button type="button" onClick={onDrawerClose} aria-label="Close menu" className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><span className="text-xl leading-none">×</span></button>
            </div>
            <NavList collapsed={false} isAdmin={isAdmin} location={location} onNavigate={onDrawerClose} />
            <Footer collapsed={false} email={user?.email} role={user?.role} onLogout={onLogout} />
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
